import produce, { Draft } from 'immer'
import { TrackJS } from 'trackjs'

import {
  BrandMode,
  LookupOptionValues,
  LookupValues,
  SearchFunc,
  SearchMode,
  UiActionTypes,
  UiState,
} from '../types/ui'
import {
  ADD_BULK_EDIT_ERROR,
  ADD_BULK_EDIT_SUCCESS,
  RESET_BULK_EDIT_RESULTS,
  SET_BATCH_LOOKUP_STAGE,
  SET_BATCH_LOOKUP_TIMEOUTS,
  SET_BATCH_LOOKUP_VALUES,
  SET_BATCH_LOOKUP_VALUES_ALIAS,
  SET_BATCH_PROGRESS_ERROR,
  SET_CLIPBOARD_PRODUCT,
  SET_CURRENT_BATCH_ID,
  SET_CURRENT_USER,
  OAUTH_LOGIN_REDIRECT,
  SET_DIRECT_URL,
  SET_GETTING_PRODUCT_ID,
  SET_INITIAL_PRODUCT_VALUES,
  SET_IS_LOADING,
  SET_IS_POSTING_PRODUCT,
  SET_LAST_ACTION,
  SET_LOOKUP_VALUES,
  SET_SEARCH_FUNC,
  SET_ENABLE_DEEP_MATCHER,
  UPDATE_BATCH_PROGRESS,
} from '../actions/ui'
import { QUANTITY_UNIT_OPTIONS } from '../../consts'
import { ProductForm } from '../types/products'
import { loadLocal } from '../../utils/localStorage'
import { AxiosError, AxiosResponse } from 'axios'

export const getInitialLookupValues = (): LookupValues => ({
  aliasMode: false,
  name: '',
  name_mode: 'strict',
  lineage: '',
  brand: '',
  brand_mode: 'strict',
  brandSubtype: '',
  brandMode: 'phonetic' as BrandMode,
  amount: '',
  dosage: '',
  productType: '',
  productSubtype: '',
  no_amount: false,
  no_lineage: false,
  no_brand_subtype: false,
  no_product_subtype: false,
  no_dosage: false,
  twoPassSearch: false,
  includeRemoved: false,
  mode: 'or' as SearchMode,
  limit: 50,
})

export const getInitialBatchLookupOptionValues = (): LookupOptionValues => ({
  mode: 'or',
  twoPassSearch: false,
  includeRemoved: false,
  limit: 20,
})

export const getInitialProductFormValues = () =>
  ({
    id: '',
    name: '',
    brand: '',
    product_brand_id: '',
    description: '',
    kind: '',
    kind_subtype: '',
    root_subtype: '',
    category: '',
    amount: '',
    dosage: '',
    net_weight_grams: '',
    thc_dosage_milligrams: '',
    cbd_dosage_milligrams: '',
    quantity_value: '',
    quantity_units: QUANTITY_UNIT_OPTIONS[0].value,
    aliases_attributes: [],
    image_urls: [],
    removal_status: null,
    notice_status: null,
    store_count: null,
    states: [],
    store_id: null,
  } as ProductForm)

const initialState: UiState = {
  lookupValues: getInitialLookupValues(),
  lookupProductId: undefined,
  batchLookupOptionValues: getInitialBatchLookupOptionValues(),
  isLoading: false,
  directUrl: '',
  isPostingProduct: false,
  gettingProductId: undefined,
  lastAction: 'lookup',
  clipboardProduct: undefined,
  initialProductValues: getInitialProductFormValues(),
  batchLookupValues: [],
  batchProgress: [],
  batchProgressError: [],
  currentBatchId: undefined,
  batchLookupStage: 'upload',
  batchLookupTimeouts: [],
  searchFunc: loadLocal<SearchFunc>('searchFunc', ['sql', 'algolia']) || 'sql',
  enableDeepMatcher:
    loadLocal<'true' | 'false'>('enableDeepMatcher', ['true', 'false']) !== 'false',
  currentUser: undefined,
  bulkEditErrors: [],
  bulkEditSuccesses: [],
}

export const uiReducer = produce((state: Draft<UiState>, action: UiActionTypes) => {
  switch (action.type) {
    case SET_LOOKUP_VALUES:
      if (action.payload !== null) {
        if (action.payload.batchId) {
          state.lookupValues = {
            ...getInitialLookupValues(),
            ...state.batchLookupValues[parseInt(action.payload.batchId)],
            // https://app.clickup.com/t/nmycmp
            brand: state.lookupValues.brand,
          }
        } else {
          state.lookupValues = {
            ...getInitialLookupValues(),
            ...action.payload.values,
          }
        }
      } else {
        state.lookupValues = getInitialLookupValues()
      }

      return

    case RESET_BULK_EDIT_RESULTS:
      state.bulkEditErrors = []
      state.bulkEditSuccesses = []
      return

    case SET_IS_LOADING:
      state.isLoading = action.payload.status
      return

    case SET_DIRECT_URL:
      state.directUrl = action.payload.url
      return

    case SET_IS_POSTING_PRODUCT:
      state.isPostingProduct = action.payload.status
      return

    case SET_GETTING_PRODUCT_ID:
      state.gettingProductId = action.payload.productId
      return

    case SET_LAST_ACTION:
      state.lastAction = action.payload.mode
      return

    case SET_CLIPBOARD_PRODUCT:
      state.clipboardProduct = action.payload.product
      return

    case SET_INITIAL_PRODUCT_VALUES:
      if (action.payload !== null) {
        state.initialProductValues = {
          ...getInitialProductFormValues(),
          ...action.payload.values,
        }
      } else {
        state.initialProductValues = getInitialProductFormValues()
      }
      return

    case SET_CURRENT_BATCH_ID:
      if (action.payload !== null) {
        state.currentBatchId = action.payload.batchId
      } else {
        state.currentBatchId = undefined
      }
      return

    case UPDATE_BATCH_PROGRESS:
      if (action.payload !== null) {
        const batchId = parseInt(action.payload.batchId)
        if (state.batchProgress[batchId] === undefined) {
          state.batchProgress[batchId] = 0
        }
        state.batchProgress[batchId] += action.payload.increment
      } else {
        state.batchProgress = []
      }
      return

    case SET_BATCH_PROGRESS_ERROR:
      if (action.payload !== null) {
        state.batchProgressError[parseInt(action.payload.batchId)] = 1
      } else {
        state.batchProgressError = []
      }
      return

    case SET_BATCH_LOOKUP_VALUES:
      if (action.payload !== null) {
        state.batchLookupValues = action.payload.batchLookupValues
      } else {
        state.batchLookupValues = []
      }
      return

    case SET_BATCH_LOOKUP_VALUES_ALIAS:
      const bId = action.payload.batchId
      if (bId) {
        state.batchLookupValues[parseInt(bId)].alias = action.payload.alias
      }
      return

    case SET_BATCH_LOOKUP_STAGE:
      state.batchLookupStage = action.payload.stage
      return

    case SET_BATCH_LOOKUP_TIMEOUTS:
      if (action.payload !== null) {
        if (action.payload.timeouts.length === 0) {
          state.batchLookupTimeouts.forEach((timeout) => clearTimeout(timeout))
        }
        state.batchLookupTimeouts = action.payload.timeouts
      } else {
        state.batchLookupTimeouts = []
      }
      return

    case SET_SEARCH_FUNC:
      state.searchFunc = action.payload.searchFunc
      return

    case SET_ENABLE_DEEP_MATCHER:
      state.enableDeepMatcher = action.payload.enableDeepMatcher
      return

    case OAUTH_LOGIN_REDIRECT:
      const { payload: oauthLoginRedirectPayload } = action
      if (!(oauthLoginRedirectPayload as AxiosError).isAxiosError) {
        window.location.href = (oauthLoginRedirectPayload as AxiosResponse).data.url
      } else {
        window.alert('Failed to set up login flow')
      }
      return

    case SET_CURRENT_USER:
      const { payload: setCurrentUserPayload } = action
      // All `AxiosError` instances have `isAxiosError` set to `true`
      if (!(setCurrentUserPayload as AxiosError).isAxiosError) {
        // Success: payload is `AxiosResponse`, data is expected to contain username
        state.currentUser = (setCurrentUserPayload as AxiosResponse).data.username
        TrackJS.configure({ userId: state.currentUser || '' })
      } else {
        // Error: payload is `AxiosError`, we don't know the login state here
        state.currentUser = undefined
      }
      return

    case ADD_BULK_EDIT_SUCCESS:
      state.bulkEditSuccesses = [...state.bulkEditSuccesses, action.payload.id]
      return

    case ADD_BULK_EDIT_ERROR:
      state.bulkEditErrors = [
        ...state.bulkEditErrors,
        { id: action.payload.id, message: action.payload.message || '' },
      ]
      return
  }
}, initialState)
